import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// The URL is now handled by the shared api instance, but we can define the endpoint path here
const SITE_MODE_ENDPOINT = '/site-mode';

/**
 * Fetch the current site display mode from the backend.
 * Returns: { mode: 'full' | 'links_only' }
 */
export const fetchSiteMode = createAsyncThunk(
  'siteMode/fetch',
  async (_, { rejectWithValue }) => {
    try {
      // Use the shared api instance which already has the base URL and interceptors
      const res = await api.get(SITE_MODE_ENDPOINT, { timeout: 10000 });
      return res.data; // { mode: 'full' | 'links_only' }
    } catch (err) {
      console.error('[SiteMode] API Error:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        endpoint: SITE_MODE_ENDPOINT
      });
      // Fallback to 'full' mode on error to ensure app remains functional
      return { mode: 'full' };
    }
  }
);

const siteModeSlice = createSlice({
  name: 'siteMode',
  initialState: {
    mode: 'full',        // 'full' | 'links_only'
    loading: true,
    error: null,
  },
  reducers: {
    setMode: (state, action) => {
      state.mode = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSiteMode.pending, (state) => {
        // Only set loading to true if it's the very first time (it starts as true in initialState)
        // If we already have loading=false, it means we already performed the first load.
        if (state.loading === true) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchSiteMode.fulfilled, (state, action) => {
        state.loading = false;
        state.mode = action.payload?.mode || 'full';
      })
      .addCase(fetchSiteMode.rejected, (state) => {
        state.loading = false;
        state.mode = 'full'; // safe fallback
      });
  },
});

export const { setMode } = siteModeSlice.actions;
export default siteModeSlice.reducer;
